import PouchDB from "pouchdb";
import { DatabaseIdentifier } from "./manager";
import { useCallback, useEffect, useState } from "react";
import { useCurrentDatabaseManager, useDatabases } from "./useDatabases";
import { useStableValue } from "../useStableValue";
import { filterUndefined } from "../../utils/utils";
import _ from "lodash";
import { PouchOptions } from "./common";

export interface PouchListReturn<T> {
  data: PouchDB.Core.ExistingDocument<T & PouchDB.Core.AllDocsMeta>[] | null;
  loading: boolean;
  error: unknown;
}

export function usePouchAllDocs<T>(
  dependencies: DatabaseIdentifier[],
  database: PouchDB.Database<T> | undefined,
  options?: (
    | PouchDB.Core.AllDocsWithKeyOptions
    | PouchDB.Core.AllDocsWithKeysOptions
    | PouchDB.Core.AllDocsWithinRangeOptions
    | PouchDB.Core.AllDocsOptions
  ) &
    PouchOptions
): PouchListReturn<T> {
  const [data, setData] = useState<PouchDB.Core.ExistingDocument<T & PouchDB.Core.AllDocsMeta>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const mngr = useCurrentDatabaseManager();

  const options_ = useStableValue(options);
  const load = useCallback(async () => {
    if (options_?.enabled === false) {
      return;
    }
    if (database) {
      setLoading(true);
      setError(null);
      try {
        console.log("[POUCH ALLDOCS]", database.name);
        const resp = await database.allDocs({ ...options_, include_docs: true });
        const newData = filterUndefined(resp.rows.map((r) => r.doc));
        setData((d) => (_.isEqual(d, newData) ? d : newData));
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    } else {
      setData(null);
      setLoading(false);
      setError(null);
    }
  }, [database, options_]);

  useEffect(() => {
    load().then(() => {});
  }, [load]);
  useEffect(() => {
    mngr?.registerQuery(dependencies, load);
    return () => mngr?.unregisterQuery(dependencies, load);
  }, [mngr, load, dependencies]);

  return { data, loading, error };
}

export function useFormTemplates() {
  const { formTemplates } = useDatabases();
  return usePouchAllDocs(["formTemplates"], formTemplates);
}
